import { useMemo } from "react";
import type { TrackPoint } from "@/lib/client-convert";
import { resolveTrim, type TrimRange } from "@/lib/trim";
import { applyHrFixRanges, type HrFixRange } from "@/lib/hr-fix";

export type EditedView = {
  range: TrimRange;
  viewPoints: TrackPoint[];
  positions: [number, number][];
  distanceM: number;
  durationMs: number;
  avgHr: number | null;
  elevGainM: number;
  hasElevation: boolean;
};

// Derives everything the converter previews need from a (possibly trimmed,
// possibly HR-fixed) time window: GPS positions, distance/duration deltas,
// avg HR and elevation gain. Shared by the single and batch converters so the
// two can't drift. The HR fix (over every point) and the trim reduction are
// memoized separately, since trim changes far more often — dragging the trim
// handles re-runs only the cheap reduction, not a full re-fix.
export function useEditedView(points: TrackPoint[], trim?: TrimRange, hrFixRanges?: HrFixRange[]): EditedView {
  // The HR fix runs before trim, matching the export pipeline in
  // client-convert.ts, so the live preview matches the downloaded file.
  const source = useMemo(
    () => (hrFixRanges?.length ? applyHrFixRanges(points, hrFixRanges).points : points),
    [points, hrFixRanges]
  );

  return useMemo(() => {
    const r = resolveTrim(source, trim);

    const viewPoints: TrackPoint[] = [];
    const positions: [number, number][] = [];
    let hrSum = 0;
    let hrCount = 0;
    let elevGainM = 0;
    let hasElevation = false;
    let prevAlt: number | undefined;

    for (const p of source) {
      if (p.timeMs < r.startMs || p.timeMs > r.endMs) continue;
      viewPoints.push(p);
      if (p.lat !== undefined && p.lon !== undefined) positions.push([p.lat, p.lon]);
      if (p.hr !== undefined) {
        hrSum += p.hr;
        hrCount += 1;
      }
      if (p.alt !== undefined) {
        hasElevation = true;
        if (prevAlt !== undefined && p.alt > prevAlt) elevGainM += p.alt - prevAlt;
        prevAlt = p.alt;
      }
    }

    const last = viewPoints[viewPoints.length - 1];
    const first = viewPoints[0];
    const distanceM = viewPoints.length >= 2 ? (last.dist ?? 0) - (first.dist ?? 0) : 0;
    const durationMs = viewPoints.length >= 2 ? last.timeMs - first.timeMs : 0;

    return {
      range: { startSec: r.startSec, endSec: r.endSec },
      viewPoints,
      positions,
      distanceM,
      durationMs,
      avgHr: hrCount ? Math.round(hrSum / hrCount) : null,
      elevGainM: Math.round(elevGainM),
      hasElevation,
    };
  }, [source, trim]);
}
